// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { EventLink } from 'presentations/views/components/molecules/ListLinkButton';
import CopyMenuLink from 'presentations/views/components/molecules/CopyMenuLink';
import {
  dashboardHostPendingEventPath,
  eventTypePath,
  hostEventTypeEditPath,
  webPath,
  dashboardHostEventPath,
} from 'presentations/routes/path';
import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  black,
  white,
  gray01,
  gray02,
  gray04,
  gray06,
  primary,
  gray03,
} from 'presentations/views/styles/color';
import UserEventTypeTagList from 'presentations/views/components/organisms/UserEventTypeTagList';
import { transformImageUrl } from 'presentations/assets';
import { splitHostAndUsersFromEventType } from 'presentations/views/helpers/event_type';
import { Role } from 'domains/models/event_type_user/role';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { ReservationMethod } from 'domains/models/event_type/reservationMethod';
import { PendingEventListItem } from 'presentations/api/schema/pending_event';
import { isEmail } from 'infras/validator';
import EventType from 'domains/models/event_type';
import { PendingEventType } from 'domains/models/pending_event/pendingEventType';
import { currentTimeZone, formatToDateTime } from 'infras/datetime';
import { useRouter } from 'next/router';
import { LocationType } from 'domains/models/event_type/locationType';
import { formatDateTimeRange } from 'presentations/views/helpers/formatter';
import { ShareType } from 'domains/models/pending_event/shareType';
import { AttendType } from 'domains/models/event_type/attendType';

export interface Props {
  teamSlug: string;
  pendingEvent: PendingEventListItem;
  role: Role;
  onClickMenu?: any;
  onCopy?: any;
  onClickCopyMenu?: any;
  isLoading?: boolean;
  type: 'event-type' | 'one-off';
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const eventType = props.pendingEvent.eventType;
  const event = props.pendingEvent.event;
  const router = useRouter();
  const isOnetimeLink =
    props.pendingEvent.pendingEventType === PendingEventType.OnetimeLink;

  const isBookingRequest =
    props.pendingEvent.pendingEventType === PendingEventType.Request;
  const isAgency = props.pendingEvent.eventType.attendType === AttendType.Agent;

  const location =
    isBookingRequest && event
      ? {
          eventTypeId: eventType.id,
          isInPerson: event.locationId === LocationType.InPerson,
          address: null,
          isZoom: event.locationId === LocationType.Zoom,
          isGoogle: event.locationId === LocationType.GoogleMeet,
          isTeams: event.locationId === LocationType.Teams,
          isPhone: event.locationId === LocationType.Phone,
          phoneNumber: null,
          isAskInvitee: event.locationId === LocationType.AskInvitee,
          isCustom: event.locationId === LocationType.Custom,
          customLocation: event.customLocation,
        }
      : eventType.location;

  const renderContent = useMemo(() => {
    const isPoll = eventType.reservationMethod === ReservationMethod.Poll;
    const { host, users } = splitHostAndUsersFromEventType(
      eventType.users,
      eventType.reservationMethod,
    );

    let title: string = eventType.name;
    let bookingPageLinkUrl: string;

    switch (props.pendingEvent.pendingEventType) {
      case PendingEventType.OnetimeLink:
        title = props.pendingEvent.name;
        bookingPageLinkUrl = eventTypePath({
          teamSlug: props.teamSlug,
          eventTypeSlug: eventType.slug,
          onetimeLinkId: props.pendingEvent.uid,
        });
        break;
      case PendingEventType.Request:
        title = props.pendingEvent.name;
        bookingPageLinkUrl = eventTypePath({
          teamSlug: props.teamSlug,
          eventTypeSlug: eventType.slug,
        });
        break;
      case PendingEventType.Poll:
      case PendingEventType.CustomEvent:
      default:
        bookingPageLinkUrl = eventTypePath({
          teamSlug: props.teamSlug,
          eventTypeSlug: eventType.slug,
        });
        break;
    }

    return (
      <>
        <EventTitle>
          <EventTitleMain>
            {eventType.color && <EventCalendarColor color={eventType.color} />}
            <EventTitleLink target="_blank" href={bookingPageLinkUrl}>
              {title}
            </EventTitleLink>
          </EventTitleMain>
          {event && event.startedAt && event.endedAt ? (
            <EventDateTime>
              {formatDateTimeRange(
                event.startedAt,
                event.endedAt,
                event.timeZone,
              )}
            </EventDateTime>
          ) : null}
          <EventTitleSub>
            {isPoll && <TypeTitle>{t('Poll')}</TypeTitle>}
            {isBookingRequest && <TypeTitle>{t('Request')}</TypeTitle>}
            {isAgency && <TypeTitle>{t('Agency')}</TypeTitle>}
            {isOnetimeLink && (
              <OneTimeTitleLink href={hostEventTypeEditPath(eventType.uid)}>
                {eventType.name}
              </OneTimeTitleLink>
            )}
            <Created>
              {t('Created', {
                createdAt: formatToDateTime(
                  props.pendingEvent.createdAt,
                  currentTimeZone(),
                ),
              })}
            </Created>
          </EventTitleSub>
        </EventTitle>
        <UserList>
          {host && (
            <Invitor>
              <Label>
                {isPoll ? translate('Creator') : translate('Owner')}
              </Label>
              <User
                src={transformImageUrl(host.profileImage, {
                  width: 32,
                  height: 32,
                })}
                alt={host.name || undefined}
                className={'MuiAvatarGroup-avatar'}
              />
            </Invitor>
          )}
          {users.length > 0 && (
            <Invitee>
              <Label>{isPoll ? translate('Voter') : translate('Member')}</Label>
              <GroupWrap max={4}>
                {users.map((user) => (
                  <>
                    {isEmail(user.uid) ? (
                      <StyledAvatar
                        key={user.uid}
                        alt={user.name}
                        style={{
                          zIndex: 0,
                        }}
                        color={EventType.generateEventTypeColorById(
                          user.name.length,
                        )}
                        className={'MuiAvatarGroup-avatar'}
                      >
                        {user.name.charAt(0)}
                      </StyledAvatar>
                    ) : (
                      <User
                        key={user.uid}
                        src={transformImageUrl(user.profileImage, {
                          width: 32,
                          height: 32,
                        })}
                        alt={user.name || user.uid}
                        style={{
                          zIndex: 0,
                        }}
                        className={'MuiAvatarGroup-avatar'}
                      />
                    )}
                  </>
                ))}
              </GroupWrap>
            </Invitee>
          )}
        </UserList>

        <PageLinkListContainer>
          <PageLinkContainer>
            <LinkIcon name="chevron-right" />
            <PageLink href={bookingPageLinkUrl} target="_blank">
              {t('Schedule')}
            </PageLink>
          </PageLinkContainer>
          {isPoll && (
            <PageLinkContainer>
              <LinkIcon name="chevron-right" />
              <PageLink
                href={dashboardHostPendingEventPath(props.pendingEvent.uid)}
              >
                {t('PollResults')}
              </PageLink>
            </PageLinkContainer>
          )}
        </PageLinkListContainer>
      </>
    );
  }, [
    eventType,
    eventType.users,
    eventType.reservationMethod,
    isBookingRequest,
  ]);

  return (
    <Card data-intercom-target="event-type-list-item" isEnabled>
      <Content isEnabled>
        <Header>
          <Block>
            <TagList>
              <UserEventTypeTagList
                duration={eventType.duration}
                location={location}
              />
            </TagList>
            <ShownPCSite>{renderContent}</ShownPCSite>
          </Block>
        </Header>
        <ShownSPSite>{renderContent}</ShownSPSite>
      </Content>
      {isBookingRequest && props.pendingEvent.event ? (
        <ActionList>
          <StyledEventDetailContainer>
            <Link
              href={dashboardHostEventPath(props.pendingEvent.event.uid, {
                from: router.asPath,
              })}
            >
              <DocumentInfoLink>
                <CalendarIcon name="calendar-text" />
                {t('Event Detail')}
                <NewWindowIcon
                  name="checkbox-multiple-blank-outline"
                  size={fontSize(13)}
                />
              </DocumentInfoLink>
            </Link>
          </StyledEventDetailContainer>
        </ActionList>
      ) : (
        props.pendingEvent.shareType === ShareType.Url && (
          <ActionList>
            <ActionListItem data-intercom-target="event-type-list-item-copy-link">
              <CopyMenuLink
                isHideIcon
                onCopy={props.onCopy}
                copiedContent={webPath(
                  isOnetimeLink
                    ? eventTypePath({
                        teamSlug: props.teamSlug,
                        eventTypeSlug: eventType.slug,
                        onetimeLinkId: props.pendingEvent.uid,
                      })
                    : eventTypePath({
                        teamSlug: props.teamSlug,
                        eventTypeSlug: eventType.slug,
                      }),
                  router.locale,
                )}
              />
            </ActionListItem>
          </ActionList>
        )
      )}
      <MenuContainer data-intercom-target="event-type-list-item-menu">
        {props.role <= Role.Editor && !isBookingRequest && (
          <IconButton
            onClick={(e: SyntheticEvent) =>
              props.onClickMenu(e, props.pendingEvent)
            }
          >
            <SettingIcon name="settings" />
          </IconButton>
        )}
      </MenuContainer>
    </Card>
  );
}

export function LoadingComponent() {
  return (
    <Card isEnabled>
      <Content isEnabled>
        <Header>
          <Block>
            <TagList>
              <Skeleton width="100px" variant="text" />
            </TagList>
            <EventTitle>
              <Skeleton width="100%" variant="text" />
            </EventTitle>
            <UserList>
              <Invitor>
                <Skeleton width={32} height={32} variant="circular" />
              </Invitor>
            </UserList>
          </Block>
        </Header>
        <ShownSPSite></ShownSPSite>
      </Content>
    </Card>
  );
}

const GroupWrap = styled(Group)`
  .MuiAvatar-root {
    width: 32px;
    height: 32px;
    font-size: ${fontSize(14)};
    font-weight: bold;
  }
`;
const StyledAvatar = styled(Avatar)<{ color: string }>`
  width: 32px;
  height: 32px;
  font-size: ${fontSize(12)};
  font-weight: initial;

  ${({ color }) => css`
    background-color: ${color};
  `}
`;
const Card = styled.div<{ isEnabled: boolean }>`
  display: flex;
  position: relative;
  background: ${({ isEnabled }) => (isEnabled ? white : gray06)};
  padding: ${spacing(32)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  ${mediaMax.desktop} {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${spacing(24)};
  }
`;
const Content = styled.div<{ isEnabled: boolean }>`
  width: 100%;
  ${({ isEnabled }) =>
    !isEnabled &&
    css`
      opacity: 0.6;
    `}
`;
const Header = styled.div`
  display: flex;
  width: 100%;
  color: ${gray01};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const TagList = styled.div`
  margin-bottom: ${spacing(8)};
  display: flex;
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
const Block = styled.div`
  width: 100%;
  ${mediaMax.tablet} {
    margin-right: ${spacing(24)};
  }
`;
const EventTitle = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${spacing(20)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const EventTitleLink = styled(Link)`
  color: ${black};
`;
const EventTitleMain = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;
const EventTitleSub = styled.div`
  color: ${black};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: ${spacing(8)};
`;
const UserList = styled.div`
  display: flex;
  align-items: center;
`;
const Invitor = styled.div`
  margin-right: ${spacing(24)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: start;
  }
`;
const Invitee = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: start;
  }
`;
const User = styled(Avatar)`
  width: 32px;
  height: 32px;
`;
const Label = styled.span`
  margin-right: ${spacing(8)};
  color: ${gray02};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(12)};
`;

const MenuContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 ${spacing(32)};
  border-left: 1px solid ${gray04};

  ${mediaMax.desktop} {
    display: flex;
    flex-direction: row;
    justify-content: initial;
    margin-top: ${spacing(24)};
    padding: 0;
    border-left: 0;
  }
  ${mediaMax.tablet} {
    flex-direction: column;
    margin-top: ${spacing(24)};
    padding: 0;
    border-left: 0;
  }
`;
const StyledEventDetailContainer = styled.div`
  width: 213px;
  height: 52px;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const ActionListItem = styled.div`
  :not(:first-of-type) {
    margin-top: ${spacing(16)};
  }
  ${mediaMax.desktop} {
    :not(:first-of-type) {
      margin-top: 0;
      margin-left: ${spacing(20)};
    }
  }
  ${mediaMax.tablet} {
    :not(:first-of-type) {
      margin-left: 0;
      margin-top: ${spacing(16)};
    }
    &:last-of-type {
      ${mediaMax.tablet} {
        margin-bottom: 0;
      }
    }
    ${EventLink} {
      width: 100%;
    }
  }
`;
const ShownPCSite = styled.div`
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ShownSPSite = styled.div`
  display: none;
  ${mediaMax.tablet} {
    display: block;
  }
`;

const EventCalendarColor = styled.span<{ color: string }>`
  display: inline-block;
  margin-right: ${spacing(8)};
  width: 8px;
  height: 8px;
  border-radius: ${borderRadius(4)};
  background-color: ${({ color }) => color};
`;

const PageLinkListContainer = styled.div`
  display: flex;
`;

const SettingIcon = styled(Icon)`
  color: ${gray01};
`;
const PageLinkContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(16)};
  margin-right: ${spacing(16)};
`;
const LinkIcon = styled(Icon)`
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(4)};
`;
const PageLink = styled(Link)`
  color: ${black};
`;

const Created = styled.div`
  font-weight: normal;
  font-size: ${fontSize(12)};
  line-height: 1;
`;
const TypeTitle = styled.span`
  margin-right: ${spacing(8)};
`;
const EventDateTime = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  line-height: 1;
  margin: ${spacing(8)} 0;
`;
const OneTimeTitleLink = styled(Link)`
  color: ${black};
  margin-right: ${spacing(8)};
  line-height: 1;
`;
const DocumentInfoLink = styled.a`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 54px;
  margin-bottom: ${spacing(16)};
  border-radius: ${borderRadius(6)};
  line-height: 54px;
  text-align: center;
  color: ${primary};
  font-weight: ${fontWeight('bold')};
  background-color: ${gray06};
  border: none;

  ${mediaMax.tablet} {
    height: 36px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const NewWindowIcon = styled(Icon)`
  position: absolute;
  right: 12px;
  color: ${gray03};
`;
const CalendarIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  font-size: ${fontSize(20)};
`;
